const removeEmptyEntries = require('../../../../shared/utils/removeEmptyEntries');
const { FILTER, GLOBAL_FILTERS } = require('../../config/filters');
const { FLOWS, ACTIVE_FILTER_SEPARATORS } = require('../constants');
const { objectValues } = require('../index');
const {
    sortByCountDescending,
    constructResultObject,
    constructOptionObject,
    constructSearchSubCategoryOptionObject,
    constructSubCategoryAllObject,
    constructSubCategoryObject,
    constructNestedSubCategoryOptionObject,
    constructSubCategoryOptionObject,
    activeFiltersToQueryString,
    optionValidation,
    filterValidation,
    constructPairOption
} = require('./_utils');

module.exports = {
    [FILTER.DELIVERY_TIME.ID]({ subCategoryData: { filters }, activeFilters, translationsService }) {
        const { delivery_time: deliveryTimeActive } = activeFilters;
        const currentFilter = FILTER.DELIVERY_TIME;

        const isSelected = (filter, id) => (!filter && !id) ? true : !!(filter && filter === id);
        const constructOptionObject = ({ alias, id }, activeFilter) => ({ alias, id, selected: isSelected(activeFilter, id) });

        const options = currentFilter.DEFAULT_IDS.map((id) => ({ id, alias: translationsService.filters.value(FILTER.DELIVERY_TIME.ID, id) }))
            .map((option) => constructOptionObject(option, deliveryTimeActive));
        const selected = !!deliveryTimeActive;

        return constructResultObject(filters, currentFilter, {
            ...currentFilter.DEFAULT_FILTER,
            display_type: currentFilter.DISPLAY_TYPE,
            selected,
            options,
            alias: translationsService.filters.name(currentFilter.ID)
        });
    },

    [FILTER.GIG_PRICE_RANGE.ID]({ subCategoryData: { filters }, activeFilters, currency: { symbol }, translationsService }) {
        const { gig_price_range: priceRangeActive = [] } = activeFilters;
        const currentFilter = FILTER.GIG_PRICE_RANGE;

        const [min, max] = priceRangeActive;
        const options = [{
            ...removeEmptyEntries({
                min,
                max,
                selected: !!(min || max)
            })
        }];

        return constructResultObject(filters, currentFilter, { options, symbol, alias: translationsService.filters.name(currentFilter.ID) });
    },
    [FILTER.PACKAGE_INCLUDES.ID]({ subCategoryData: { filters }, activeFilters, dominateSubCategoryId, translationsService }, facetValues) {
        const { package_includes: packageIncludesActive } = activeFilters;
        const currentFilter = FILTER.PACKAGE_INCLUDES;

        const options = facetValues.filter(({ id }) => optionValidation(filters, currentFilter.ID, id))
            .map((facet) => ({ ...facet, alias: translationsService.filters.pricingFactor(facet.id, dominateSubCategoryId) }))
            .map((option) => constructOptionObject(option, packageIncludesActive))
            .sort(sortByCountDescending);

        if (!options.length) {
            return undefined;
        }

        return constructResultObject(filters, currentFilter, { options, alias: translationsService.filters.name(currentFilter.ID) });
    },

    [FILTER.PRO.ID]({ subCategoryData: { filters }, activeFilters, translationsService }, facetValues) {
        const { pro: proActive } = activeFilters;

        const currentFilter = FILTER.PRO;
        const proGigs = facetValues.find((value) => value.id === true.toString());
        const count = proGigs ? proGigs.count : 0;

        const selected = [ currentFilter.SELECTED_VALUE, currentFilter.SELECTED_VALUE_ONLY ].includes(proActive);

        const options = [{ count, selected }];

        return constructResultObject(filters, currentFilter, { options, display_type: FILTER.PRO.DISPLAY_TYPE, alias: translationsService.filters.name(currentFilter.ID) });
    },

    [FILTER.SELLER_LANGUAGE.ID]({ subCategoryData: { filters }, activeFilters, translationsService }, facetValues) {
        const { seller_language: sellerLanguageActive } = activeFilters;
        const currentFilter = FILTER.SELLER_LANGUAGE;

        const options = facetValues.filter(({ id }) => currentFilter.WHITELIST[id])
            .map((facet) => ({ ...facet, alias: translationsService.filters.value(currentFilter.ID, facet.id) }))
            .map((option) => constructOptionObject(option, sellerLanguageActive))
            .sort(sortByCountDescending);

        return constructResultObject(filters, currentFilter, { options, alias: translationsService.filters.name(currentFilter.ID) });
    },

    [FILTER.SELLER_LEVEL.ID]({ subCategoryData: { filters }, activeFilters, translationsService }, facetValues) {
        const { seller_level: sellerLevelActive } = activeFilters;
        const currentFilter = FILTER.SELLER_LEVEL;

        const options = currentFilter.DEFAULT_IDS.map((defaultId) => {
            const matchingFacet = facetValues.find(({ id }) => id === defaultId) || {};

            return { id: defaultId, count: 0, alias: translationsService.filters.value(currentFilter.ID, defaultId), ...matchingFacet };
        })
            .map((option) => constructOptionObject(option, sellerLevelActive));

        return constructResultObject(filters, currentFilter, { options, alias: translationsService.filters.name(currentFilter.ID) });
    },

    [FILTER.SELLER_LOCATION.ID]({ subCategoryData: { filters }, activeFilters, translationsService }, facetValues) {
        const { seller_location: sellerLocationActive } = activeFilters;
        const currentFilter = FILTER.SELLER_LOCATION;

        const options = facetValues.filter(({ id }) => currentFilter.WHITELIST[id])
            .map((facet) => ({ ...facet, alias: translationsService.filters.value(currentFilter.ID, facet.id) }))
            .map((option) => constructOptionObject(option, sellerLocationActive))
            .sort(sortByCountDescending);

        return constructResultObject(filters, currentFilter, { options, alias: translationsService.filters.name(currentFilter.ID) });
    },

    [FILTER.IS_SELLER_ONLINE.ID]({ subCategoryData: { filters }, activeFilters, translationsService }, facetValues) {
        const { is_seller_online: sellerOnlineActive } = activeFilters;
        const currentFilter = FILTER.IS_SELLER_ONLINE;

        const onlineSellers = facetValues.find((value) => value.id === true.toString());
        const options = [{
            count: onlineSellers ? onlineSellers.count : 0,
            selected: (sellerOnlineActive || sellerOnlineActive === true.toString()),
            alias: translationsService.filters.value(currentFilter.ID, true.toString())
        }];

        return constructResultObject(filters, currentFilter, { options, alias: translationsService.filters.name(currentFilter.ID) });
    },

    [FILTER.SUBSCRIPTION.ID]({ subCategoryData: { filters }, activeFilters, translationsService }, facetValues) {
        const { subscription } = activeFilters;
        const currentFilter = FILTER.SUBSCRIPTION;
        const subscriptionServices = facetValues.find((value) => value.id === true.toString());

        const options = [{
            count: subscriptionServices ? subscriptionServices.count : 0,
            selected: (subscription || subscription === true.toString()),
            alias: translationsService.filters.value(currentFilter.ID, true.toString())
        }];

        return constructResultObject(filters, currentFilter,
            { options, alias: translationsService.general.translate('search_perseus.filters.global.subscription.title'), display_type: currentFilter.DISPLAY_TYPE });
    },

    [FILTER.CUSTOM_FILTER.ID]({ subCategoryData: { filters, allowed_nested = [] }, activeFilters, translationsService, dominateSubCategoryId, categoryIds: { nestedSubCategoryId } }, facetValues, filterId, { filterExcludes }) {
        const currentFilter = FILTER.CUSTOM_FILTER;

        if (!filterValidation(filters, filterId, filterExcludes, allowed_nested)) {
            return;
        }
        const options = facetValues.filter(({ id }) => optionValidation(filters, filterId, id))
            .map((facet) => ({ ...facet, alias: translationsService.filters.value(filterId, facet.id, dominateSubCategoryId, nestedSubCategoryId) }))
            .map((option) => constructOptionObject(option, activeFilters[filterId]))
            .sort(sortByCountDescending);

        return constructResultObject(filters, { ...currentFilter, ID: filterId }, { options, alias: translationsService.filters.name(filterId, dominateSubCategoryId, nestedSubCategoryId) });
    },

    [FILTER.LANGUAGES_PAIR.ID]({ subCategoryData: { filters }, activeFilters, translationsService, dominateSubCategoryId }, facetValues) {
        const { languages_pair: languagesPairActive = FILTER.LANGUAGES_PAIR.INITIAL } = activeFilters;
        const currentFilter = FILTER.LANGUAGES_PAIR;
        const [fromValue, toValue] = languagesPairActive;
        const { fromOptions, toOptions } = facetValues.reduce(({ fromOptions, toOptions }, facet) => {

            const { id, count } = facet;
            const [fromLanguage, toLanguage] = id.split(ACTIVE_FILTER_SEPARATORS.PAIR_VALUE);

            if (fromLanguage && toLanguage) {
                fromOptions[fromLanguage] = constructPairOption({ id: fromLanguage, count, activeValue: fromValue, filters, existingOption: fromOptions[fromLanguage], alias: translationsService.filters.value(currentFilter.ID, fromLanguage, currentFilter.SUB_CATEGORY_ID) });
                toOptions[toLanguage] = constructPairOption({ id: toLanguage, count, activeValue: toValue, filters, existingOption: toOptions[toLanguage], alias: translationsService.filters.value(currentFilter.ID, toLanguage, currentFilter.SUB_CATEGORY_ID) });
            }

            return { fromOptions, toOptions };
        }, { fromOptions: {}, toOptions: {} });

        return constructResultObject(filters, currentFilter,
            {
                options: {
                    fromOptions: objectValues(fromOptions),
                    toOptions: objectValues(toOptions)
                },
                alias: translationsService.filters.name(currentFilter.ID, dominateSubCategoryId)
            }
        );
    },

    [FLOWS.CATEGORY](context, { sub_categories: subCategories = [], nested_sub_categories: nestedSubCategories = [] }) {
        const { categoryIds, subCategoryData, translationsService } = context;
        const { subCategoryId, nestedSubCategoryId } = categoryIds;

        const slugs = {
            category: subCategoryData.category.cached_slug,
            subCategory: subCategoryData.cached_slug
        };

        const currentSubCategory = subCategories.find(({ id }) => id === subCategoryId) || {};
        const subCategoryCount = nestedSubCategoryId ? nestedSubCategories.reduce((sum, { count }) => sum + count, 0) : currentSubCategory.count;

        const all = constructSubCategoryAllObject(translationsService.general.translate('search_perseus.filter_builder.sub_category.all_in', { category: subCategoryData.category.name }), { category: subCategoryData.category.cached_slug });

        const subCategory = constructSubCategoryObject(subCategoryCount, slugs, context);
        const enrichedNestedSubCategories = getEnrichedNestedSubCategories(subCategoryData, nestedSubCategories);

        const options = enrichedNestedSubCategories
            .map(({ id, count, src }) => constructNestedSubCategoryOptionObject(id, count, src, slugs, context))
            .filter((opt) => !!opt.alias);

        return {
            ...all,
            globalFiltersQuery: getGlobalActiveFiltersQuery(context),
            children: [{ ...subCategory, children: options }]
        };
    },

    [FLOWS.PRO_CATEGORY](context, { sub_categories: subCategories = [] }) {
        const { categoryIds, subCategoryData, translationsService } = context;
        const { subCategoryId } = categoryIds;

        const slugs = {
            category: subCategoryData.category.cached_slug,
            subCategory: subCategoryData.cached_slug
        };

        const currentSubCategory = subCategories.find(({ id }) => id === subCategoryId) || {};
        const subCategoryCount = currentSubCategory.count;

        const all = constructSubCategoryAllObject(translationsService.general.translate('search_perseus.filter_builder.sub_category.all_in', { category: subCategoryData.category.name }), { category: subCategoryData.category.cached_slug });

        const subCategory = constructSubCategoryObject(subCategoryCount, slugs, context);

        return {
            ...all,
            globalFiltersQuery: getGlobalActiveFiltersQuery(context),
            children: [{ ...subCategory, children: [] }]
        };
    },
    [FLOWS.BUSINESS_CATEGORIES](context, { sub_categories: subCategories = [], nested_sub_categories: nestedSubCategories = [] }) {
        const { categoryIds, subCategoryData, translationsService } = context;
        const { subCategoryId, nestedSubCategoryId } = categoryIds;

        const slugs = {
            category: subCategoryData.category.cached_slug,
            subCategory: subCategoryData.cached_slug
        };

        const currentSubCategory = subCategories.find(({ id }) => id === subCategoryId) || {};
        const subCategoryCount = nestedSubCategoryId ? nestedSubCategories.reduce((sum, { count }) => sum + count, 0) : currentSubCategory.count;

        const all = constructSubCategoryAllObject(translationsService.general.translate('search_perseus.filter_builder.sub_category.all_in', { category: subCategoryData.category.name }), { category: subCategoryData.category.cached_slug });

        const subCategory = constructSubCategoryObject(subCategoryCount, slugs, context);
        const enrichedNestedSubCategories = getEnrichedNestedSubCategories(subCategoryData, nestedSubCategories);

        const options = enrichedNestedSubCategories
            .map(({ id, count, src }) => constructNestedSubCategoryOptionObject(id, count, src, slugs, context))
            .filter((opt) => !!opt.alias);

        return {
            ...all,
            globalFiltersQuery: getGlobalActiveFiltersQuery(context),
            children: [{ ...subCategory, children: options }]
        };
    },
    [FLOWS.SEARCH](context, { sub_categories: subCategories = [] }) {
        const { translationsService } = context;
        const all = constructSearchSubCategoryOptionObject(translationsService.general.translate('search_perseus.filter_builder.sub_category.all_categories'), context);
        const options = subCategories.map(({ id, count }) => constructSubCategoryOptionObject(id, count, context))
            .filter((opt) => !!opt.alias);

        return {
            ...all,
            globalFiltersQuery: getGlobalActiveFiltersQuery(context),
            children: options
        };
    },
    [FLOWS.BUSINESS_SEARCH](context, { sub_categories: subCategories = [] }) {
        const { translationsService } = context;
        const all = constructSearchSubCategoryOptionObject(translationsService.general.translate('search_perseus.filter_builder.sub_category.all_categories'), context);
        const options = subCategories.map(({ id, count }) => constructSubCategoryOptionObject(id, count, context))
            .filter((opt) => !!opt.alias);

        return {
            ...all,
            globalFiltersQuery: getGlobalActiveFiltersQuery(context),
            children: options
        };
    }
};

const getGlobalActiveFiltersQuery = ({ activeFilters }) => {

    const filterKeys = Object.keys(activeFilters).filter((key) => GLOBAL_FILTERS.includes(key));
    const globalFilter = filterKeys.reduce((filter, key) => {
        filter[key] = activeFilters[key];
        return filter;
    }, {});

    return activeFiltersToQueryString(globalFilter);
};

const getEnrichedNestedSubCategories = (subCategoryData, nestedSubCategories) => nestedSubCategories.map((nsc) => {
    const nscData = subCategoryData.nestedSubCategories.find(({ id }) => id === parseInt(nsc.id, 10));

    if (!nscData) { return nsc; }

    return {
        ...nsc,
        src: nscData.src
    };
});
